/**
 * ActivitySubmissionsV2 - Programming/Virtual Activity Submissions with modern design
 * Features:
 * - Summary statistics cards (Total Students, Submissions, Evaluated, Pending, etc.)
 * - Search bar with debounce
 * - Filters dropdown (Submission Status, Evaluation Status, Rubric Status, Validity)
 * - View dropdown for column toggling
 * - Actions: Run, Force Run, Download, Submit As, Plagiarism Check, TA Allocation, TA Buddy, Deposit Data
 * - Dynamic testcase columns
 * - Server-side pagination and sorting
 */
import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Breadcrumb, Dropdown, Spinner, OverlayTrigger, Tooltip, Modal, ProgressBar, Button, Form } from 'react-bootstrap';
import { useDebounce } from 'use-debounce';
import * as constants from 'Constants';
import httpService from 'services/httpService';
import fileDownload from 'js-file-download';
import displayGlobalMessage from 'Utils/DisplayGlobalMessage';
import { loadCoursesListRequest } from 'Screens_old/PublicCourses/Common/Actions/Actions';
import { loadCoursesListRequest as instructor_loadCoursesListRequest } from 'Store/MyDashboard/Instructor/Actions';
import { toast } from 'react-toastify';
import log from 'loglevel';

// Import existing components
import DownloadSingleProgrammingActivityCSV from 'Components/Instructor/Lab/ProgrammingActivity/DownloadSingleProgrammingActivityCSV';
import BulkSubmissionModal from 'Navigation/Course/Content/Programming/Instructor/AllSubmissions/BulkSubmissionModal';
import TaBuddy from 'Navigation/Course/Content/Programming/Instructor/AllSubmissions/TaBuddy';
import TestCaseModal from 'Navigation/Course/Content/Programming/Instructor/AllSubmissions/TestCaseModal';

// Icons
import { FiUsers, FiSearch, FiChevronDown, FiDownload, FiFilter, FiPlay, FiCheck, FiX, FiChevronUp } from 'react-icons/fi';
import { BsClipboardCheck, BsFileEarmarkCode } from 'react-icons/bs';
import { HiOutlineClock } from 'react-icons/hi';
import { MdOutlinePending, MdOutlineGrading, MdOutlinePlaylistAddCheck } from 'react-icons/md';
import { BiError } from 'react-icons/bi';
import { AiOutlineCloudDownload } from 'react-icons/ai';
import { IoIosInformationCircle } from 'react-icons/io';
import { GiCancel } from 'react-icons/gi';

import styles from './ActivitySubmissionsV2.module.css';

const PAGE_SIZE = 100;

// Memoized StatCard to prevent unnecessary re-renders
const StatCard = memo(({ label, value, icon: Icon, colorClass }) => (
  <div className={styles.statCard}>
    <div className={styles.statCardContent}>
      <span className={styles.statCardLabel}>{label}</span>
      <span className={`${styles.statCardValue} ${styles[colorClass]}`}>{value}</span>
    </div>
    <div className={`${styles.statCardIcon} ${styles[colorClass + 'Bg']}`}>
      <Icon size={18} />
    </div>
  </div>
));

// Memoized Statistics Row component
const StatsRow = memo(({ submissionsSummary }) => (
  <div className={styles.statsRow}>
    <StatCard
      label="TOTAL STUDENTS"
      value={submissionsSummary.total_students || 0}
      icon={FiUsers}
      colorClass="blue"
    />
    <StatCard
      label="TOTAL SUBMISSIONS"
      value={submissionsSummary.total_submissions || 0}
      icon={BsClipboardCheck}
      colorClass="green"
    />
    <StatCard
      label="EVALUATION PENDING"
      value={submissionsSummary.evaluation_pending || 0}
      icon={MdOutlinePending}
      colorClass="orange"
    />
    <StatCard
      label="EVALUATED"
      value={submissionsSummary.evaluated || 0}
      icon={MdOutlinePlaylistAddCheck}
      colorClass="cyan"
    />
    <StatCard
      label="RUBRIC PENDING"
      value={submissionsSummary.rubric_grading_pending || 0}
      icon={MdOutlineGrading}
      colorClass="purple"
    />
    <StatCard
      label="RUBRIC GRADED"
      value={submissionsSummary.rubric_graded || 0}
      icon={FiCheck}
      colorClass="teal"
    />
    <StatCard
      label="INVALID"
      value={submissionsSummary.invalid || 0}
      icon={BiError}
      colorClass="red"
    />
  </div>
));

// Memoized Breadcrumb component
const BreadcrumbNav = memo(({ courseId, courseName, labID, labTitle, actType, programId, activityTitle }) => (
  <Breadcrumb className={styles.breadcrumb}>
    <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/courseware/course/${courseId}` }}>
      {courseName || 'Course'}
    </Breadcrumb.Item>
    <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/courseware/course/${courseId}/content/labs` }}>
      Labs
    </Breadcrumb.Item>
    <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/courseware/course/${courseId}/content/labs/${labID}/` }}>
      {labTitle || 'Lab'}
    </Breadcrumb.Item>
    <Breadcrumb.Item
      linkAs={Link}
      linkProps={{ to: `/courseware/course/${courseId}/content/labs/${labID}/${actType}/${programId}/view` }}
    >
      {activityTitle || 'Activity'}
    </Breadcrumb.Item>
    <Breadcrumb.Item active>Submissions</Breadcrumb.Item>
  </Breadcrumb>
));

const ActivitySubmissionsV2 = ({ submissionData, setSubmissionData }) => {
  const { courseId, labId, activityType, activityID } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const navEl = document.querySelector('.sticky-top');
    const navH = navEl ? navEl.getBoundingClientRect().height : 60;
    document.documentElement.style.setProperty('--bt-navbar-h', `${navH}px`);
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Extract IDs from URL if not in params
  const urlArray = location.pathname.split('/');
  const programId = activityID || urlArray[8];
  const labID = labId || urlArray[6];
  const actType = activityType || urlArray[7];

  // Data state
  const [submissionsSummary, setSubmissionsSummary] = useState({});
  const [submissions, setSubmissions] = useState([]);
  const [testcases, setTestcases] = useState({ visible: [], hidden: [] });
  const [activityTitle, setActivityTitle] = useState('');
  const [labTitle, setLabTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [listOfAllSubmissions, setListOfAllSubmissions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isLoadingMoreRef = useRef(false);
  const tableWrapperRef = useRef(null);

  // Selection state
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  // Search, sort, and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  // Filter state
  const [filters, setFilters] = useState({
    submissionStatus: '',
    evaluationStatus: '',
    rubricStatus: '',
    validity: '',
  });

  // Column visibility state - all columns visible by default
  const [visibleColumns, setVisibleColumns] = useState({
    rollNo: true,
    email: true,
    name: true,
    uploadedOn: true,
    evaluatedOn: true,
    fileSubmitted: true,
    ta: true,
    gradedBy: true,
    gradedOn: true,
    testcases: true,
    practiceMarks: true,
    evaluateMarks: true,
    grade: true,
    rubricMarks: true,
    manualMarks: true,
    executionTime: true,
    indentation: true,
    totalMarks: true,
    submissionStatus: true,
  });

  // Dropdown states
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);

  // Testcase modal state
  const [showTestcaseModal, setShowTestcaseModal] = useState(false);
  const [testcaseModalData, setTestcaseModalData] = useState({
    submissionID: null,
    testcaseType: '',
    studentName: '',
    studentEmail: '',
    obtainedMarks: 0,
    studentID: null,
  });

  // Error modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState({
    firstName: '',
    email: '',
    errorMessage: '',
    errorType: '',
  });

  // Execution state
  const [isInQueue, setIsInQueue] = useState(false);
  const [celeryTaskId, setCeleryTaskId] = useState(null);
  const [numOfTasks, setNumOfTasks] = useState(null);
  const [progressPercentage, setProgressPercentage] = useState(0);

  // Bulk submission modal
  const [showPopupBulkSubmission, setShowPopupBulkSubmission] = useState(false);
  const [bulkSubmissionStatus, setBulkSubmissionStatus] = useState(false);

  // Plagiarism state
  const [newReqModal, setNewReqModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [plagiarismType, setPlagiarismType] = useState('');
  const [plagiarismReqData, setPlagiarismReqData] = useState([]);
  const [coursesForPlag, setCoursesForPlag] = useState([]);
  const [courseSelectedForPlag, setCourseSelectedForPlag] = useState('');
  const [activitySelectedForPlag, setActivitySelectedForPlag] = useState(0);
  const [courseMap, setCourseMap] = useState({});
  const [programIdMap, setProgramIdMap] = useState({});
  const [selectedSubmissionsForPlag, setSelectedSubmissionsForPlag] = useState([]);
  const [fettState, setFettState] = useState(false);

  // TA Allocation state


  // TA Buddy state
  const [newReqTABuddyModal, setNewReqTABuddyModal] = useState(false);
  const [showRequestTABuddyModal, setShowRequestTABuddyModal] = useState(false);
  const [criteriaState, setCriteriaState] = useState('All criteria');
  const [aiGradingRequestDetail, setAiGradingRequestDetail] = useState([]);
  const [criteriasWithAiGrading, setCriteriasWithAiGrading] = useState(['All criteria']);
  const [aiGradingList, setAiGradingList] = useState([]);

  // Deposit data state
  const [dataExport, setDataExport] = useState(false);

  // Access control
  const [accessControlVars, setAccessControlVars] = useState({});

  // Course name
  const [courseName, setCourseName] = useState('');
  const instructorCourses = useSelector((state) => state.instructorCoursesMap);
  const courses = useSelector((state) => state.publicCoursesMap);

  // Load course lists
  useEffect(() => {
    dispatch(loadCoursesListRequest());
    dispatch(instructor_loadCoursesListRequest());
  }, [dispatch]);

  // Sync submissions to shared state and localStorage for navigation in grading page
  useEffect(() => {
    if (submissions && submissions.length > 0) {
      setSubmissionData?.(submissions);
      localStorage.setItem('submissionData', JSON.stringify(submissions));
    }
  }, [submissions, setSubmissionData]);

  // Get course name
  useEffect(() => {
    let tempCourse;
    if (courses?.length > 0) {
      tempCourse = courses.find((item) => item.id === Number(courseId));
      if (tempCourse) setCourseName(tempCourse.title);
    }
    if (!tempCourse && instructorCourses?.length > 0) {
      tempCourse = instructorCourses.find((item) => item.id === Number(courseId));
      if (tempCourse) setCourseName(tempCourse.title);
    }
  }, [courses, instructorCourses, courseId]);

  // Fetch activity title
  useEffect(() => {
    const fetchActivityTitle = async () => {
      if (!programId || !actType) return;
      try {
        const url = `${constants.base_url}/lab/activity/${programId}?type=${actType}`;
        const response = await httpService.get(url);
        const activityData = response.data?.program_data || response.data?.other_activity;
        setActivityTitle(activityData?.name || activityData?.title || 'Activity');
      } catch (error) {
        log.error('Error fetching activity title:', error);
      }
    };
    fetchActivityTitle();
  }, [programId, actType]);

  // Fetch lab title
  useEffect(() => {
    const fetchLabTitle = async () => {
      if (!courseId || !labID) return;
      try {
        const url = `${constants.base_url}/lab/all/${labID}?courseId=${courseId}`;
        const response = await httpService.get(url);
        setLabTitle(response.data.lab?.title || 'Lab');
      } catch (error) {
        log.error('Error fetching lab title:', error);
      }
    };
    fetchLabTitle();
  }, [labID, courseId]);

  // Track if this is the initial load
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Reset to page 1 when search/sort/filter changes
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    setSubmissions([]);
  }, [debouncedSearchQuery, sortField, sortOrder, filters, bulkSubmissionStatus]);

  // Fetch submissions data
  useEffect(() => {
    const fetchSubmissions = async () => {
      const isFirstPage = currentPage === 1;
      if (isFirstPage) {
        if (isInitialLoad) {
          setLoading(true);
        } else {
          setTableLoading(true);
        }
      } else {
        setIsLoadingMore(true);
      }

      try {
        const sortParam = sortOrder === 'desc' ? '-' : '';
        let url = `${constants.base_url}/evalpro/submissions/${programId}?page=${currentPage}&page_size=${PAGE_SIZE}&search_query=${debouncedSearchQuery}&sort=${sortParam}${sortField}`;

        if (filters.submissionStatus) url += `&submission_status_filter=${filters.submissionStatus}`;
        if (filters.evaluationStatus) url += `&evaluation_status_filter=${filters.evaluationStatus}`;
        if (filters.rubricStatus) url += `&rubric_status_filter=${filters.rubricStatus}`;
        if (filters.validity) url += `&validity_filter=${filters.validity}`;

        const response = await httpService.get(url);
        const data = response.data.submissions;
        const newResults = data.submissions?.results || [];
        const count = data.submissions?.count || 0;

        if (isFirstPage) {
          setSubmissionsSummary(data);
          setSubmissions(newResults);
          setTestcases({
            visible: data.testcases?.visible || data.testcases?.visible_testcases || [],
            hidden: data.testcases?.hidden || data.testcases?.hidden_testcases || [],
          });

          // Set access control vars from submissions API
          if (response.data.access_control_vars) {
            setAccessControlVars(response.data.access_control_vars);
          }

          // Store all submission IDs for run all functionality
          const allIds = data.submission_id_list || newResults.map(s => s.id);
          setListOfAllSubmissions(allIds);
        } else {
          setSubmissions((prev) => [...prev, ...newResults]);
          // Accumulate submission IDs for run all
          const newIds = data.submission_id_list || newResults.map(s => s.id);
          setListOfAllSubmissions((prev) => [...prev, ...newIds]);
        }
        setTotalCount(count);
        setHasMore(currentPage * PAGE_SIZE < count);
      } catch (error) {
        toast.error(error.response?.data?.error_msg || 'Error fetching submissions', {
          position: 'top-right',
          autoClose: 3000,
        });
      } finally {
        setLoading(false);
        setTableLoading(false);
        setIsInitialLoad(false);
        setIsLoadingMore(false);
        isLoadingMoreRef.current = false;
      }
    };

    if (programId) {
      fetchSubmissions();
    }
  }, [programId, currentPage, debouncedSearchQuery, sortField, sortOrder, filters, bulkSubmissionStatus]);

  // Execution progress status check
  useEffect(() => {
    if (celeryTaskId !== null && numOfTasks !== null) {
      getExecutionProgressStatus();
    }
  }, [celeryTaskId, numOfTasks]);

  // Fetch rubrics for TA Buddy criteria list
  useEffect(() => {
    const fetchRubricData = async () => {
      if (!programId) return;
      const url = `${constants.base_url}/evalpro/rubric/read_rubric/${programId}`;
      try {
        const response = await httpService.get(url);
        // Extract the criteria names from response.data
        const updatedCriterias = response.data.map((criteria) => criteria.title);
        // Update the criteriasWithAiGrading state
        setCriteriasWithAiGrading(['All criteria', ...updatedCriterias]);
      } catch (error) {
        log.error('Error fetching rubric data:', error);
      }
    };

    fetchRubricData();
  }, [programId]);

  const getExecutionProgressStatus = () => {
    const url = `${constants.base_url}/evalpro/evaluate/checkstatus/${programId}`;
    const formData = new FormData();
    formData.append('task_id', celeryTaskId);
    formData.append('num_of_tasks', numOfTasks);

    const intervalId = setInterval(async () => {
      try {
        const response = await httpService.put(url, formData);
        setProgressPercentage(response.data?.progress || 0);

        const progress = Number(response.data?.progress);
        if (progress >= 100) {
          clearInterval(intervalId);
          setTimeout(() => window.location.reload(), 2000);
        }
      } catch (error) {
        log.error(error);
        clearInterval(intervalId);
      }
    }, 2000);
  };

  // Handle select all
  const handleSelectAll = useCallback(
    (checked) => {
      setSelectAll(checked);
      setIsSelectAllChecked(checked);
      if (checked) {
        setSelectedSubmissions(submissions);
      } else {
        setSelectedSubmissions([]);
      }
    },
    [submissions]
  );

  // Handle row selection
  const handleRowSelect = useCallback((submission, checked) => {
    if (checked) {
      setSelectedSubmissions((prev) => [...prev, submission]);
    } else {
      setSelectedSubmissions((prev) => prev.filter((s) => s.id !== submission.id));
      setSelectAll(false);
      setIsSelectAllChecked(false);
    }
  }, []);

  // Handle infinite scroll
  const handleScroll = useCallback(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper || isLoadingMoreRef.current || !hasMore) return;
    if (wrapper.scrollTop + wrapper.clientHeight >= wrapper.scrollHeight - 100) {
      isLoadingMoreRef.current = true;
      setCurrentPage((prev) => prev + 1);
    }
  }, [hasMore]);

  // Execute submissions (Run / Force Run)
  const executeSubmission = async (force, testcaseType) => {
    setIsInQueue(true);
    let submissionList = [];

    if (selectedSubmissions.length === 0 || isSelectAllChecked) {
      submissionList = listOfAllSubmissions;
    } else {
      submissionList = selectedSubmissions
        .map((submission) => submission.id)
        .filter((id) => id !== undefined && id !== null);
    }

    const formData = new FormData();
    formData.append('force', force);
    formData.append('type', testcaseType);
    formData.append('submissions', submissionList);

    const url = `${constants.base_url}/evalpro/evaluate/${programId}`;

    try {
      const response = await httpService.put(url, formData);

      if (!force) {
        if (response.data.submissions.message === 'No submissions to evaluate') {
          displayGlobalMessage('All selected submissions are already Evaluated', 'error');
        }
        setIsInQueue(false);
      }

      setCeleryTaskId(response.data?.submissions?.task_id);
      setNumOfTasks(response.data?.submissions?.num_of_tasks);
    } catch (error) {
      log.error(error);
      setIsInQueue(false);
      displayGlobalMessage('Error starting evaluation', 'error');
    }
  };

  // Handle Run click
  const handleRun = (testcaseType) => {
    const visibleTestcases = testcases.visible || [];
    const hiddenTestcases = testcases.hidden || [];

    if (testcaseType === 'visible' && visibleTestcases.length === 0) {
      displayGlobalMessage('No visible testcases added', 'error');
      return;
    }
    if (testcaseType === 'hidden' && hiddenTestcases.length === 0) {
      displayGlobalMessage('No hidden testcases added', 'error');
      return;
    }
    if (testcaseType === 'all' && visibleTestcases.length === 0 && hiddenTestcases.length === 0) {
      displayGlobalMessage('No testcases added', 'error');
      return;
    }
    executeSubmission(false, testcaseType);
  };

  // Handle Force Run click
  const handleForceRun = (testcaseType) => {
    const visibleTestcases = testcases.visible || [];
    const hiddenTestcases = testcases.hidden || [];

    if (testcaseType === 'visible' && visibleTestcases.length === 0) {
      displayGlobalMessage('No visible testcases added', 'error');
      return;
    }
    if (testcaseType === 'hidden' && hiddenTestcases.length === 0) {
      displayGlobalMessage('No hidden testcases added', 'error');
      return;
    }
    if (testcaseType === 'all' && visibleTestcases.length === 0 && hiddenTestcases.length === 0) {
      displayGlobalMessage('No testcases added', 'error');
      return;
    }
    executeSubmission(true, testcaseType);
  };

  // Plagiarism functions
  const getPlagiarismRequests = async () => {
    try {
      const response = await httpService.get(
        `${constants.base_url}/evalpro/get_plagiarism/${programId}/program`
      );
      const data = response.data.data;
      const prevRequests = data.map((item) => ({
        status: item.temp ? 'Pending' : 'View',
        created_at: item.creation_time,
        plag_checker: item.plagiarism_type,
        files: item.programs.map(p => `${p.course_title}-${p.course_year}-${p.lab_title}-${p.program_title}`).join('\n'),
        ids: item.programs.map(p => p.program_id),
      }));
      setPlagiarismReqData(prevRequests);
    } catch (error) {
      log.error(error);
    }
  };

  // Get all courses and activities for plagiarism check
  const getPlagCoursesAndActivities = async () => {
    try {
      const response = await httpService.get(
        `${constants.base_url}/evalpro/plagiarism/${programId}/program`
      );
      const data = response.data.data;
      const course_map = {};
      const program_id_map = {};

      data.forEach((item) => {
        program_id_map[item.program_id] = item;

        // Auto-add current activity to selected submissions
        if (item.program_id === parseInt(programId)) {
          if (!selectedSubmissionsForPlag.some((submission) => submission.program_id === item.program_id)) {
            setSelectedSubmissionsForPlag((prev) => [...prev, item]);
          }
        } else {
          const key = `${item.course_title}-${item.course_year}`;
          if (!course_map[key]) {
            course_map[key] = [];
          }
          course_map[key].push(item);
        }
      });

      setCoursesForPlag(Object.keys(course_map));
      setProgramIdMap(program_id_map);
      setCourseMap(course_map);
    } catch (error) {
      log.error(error);
    }
  };

  // Handle adding submission for plagiarism check
  const handleAddSubmissionPlag = () => {
    if (courseSelectedForPlag && courseSelectedForPlag !== '' && parseInt(activitySelectedForPlag) > 0) {
      if (selectedSubmissionsForPlag.some((item) => item.program_id === parseInt(activitySelectedForPlag))) {
        displayGlobalMessage('Activity already added', 'error');
        return;
      }
      setSelectedSubmissionsForPlag([
        ...selectedSubmissionsForPlag,
        programIdMap[activitySelectedForPlag],
      ]);
    } else {
      displayGlobalMessage('Select Activity to add', 'error');
    }
  };

  // Handle removing submission from plagiarism check
  const handleRemoveSubmissionPlag = (pid) => {
    if (pid === parseInt(programId)) {
      displayGlobalMessage('Cannot remove current Activity submission', 'error');
      return;
    }
    const indexToRemove = selectedSubmissionsForPlag.findIndex((item) => item.program_id === pid);
    if (indexToRemove > -1) {
      const newList = [...selectedSubmissionsForPlag];
      newList.splice(indexToRemove, 1);
      setSelectedSubmissionsForPlag(newList);
    }
  };

  // Handle modal close
  const handlePlagModalClose = () => {
    setPlagiarismType('');
    setFettState(false);
    setNewReqModal(false);
  };

  const handleTABuddyModalClose = () => {
    setNewReqTABuddyModal(false);
  };

  const handleSubmitPlagRequest = async () => {
    if (plagiarismType === '') {
      displayGlobalMessage('Select a Plagiarism Checker', 'error');
      return;
    }
    try {
      const url = `${constants.base_url}/evalpro/plagiarism/${programId}/${plagiarismType}/program`;
      const response = await httpService.post(url, {
        ids: selectedSubmissionsForPlag.map((item) => item.program_id),
      });

      toast.info('Plagiarism Request Added to Queue!', {
        position: 'top-right',
        autoClose: 3000,
      });

      const taskId = response.data.data.task_id;
      checkPlagRequestStatus(programId, taskId);
      getPlagiarismRequests();
      setPlagiarismType('');
      setFettState(false);
    } catch (error) {
      log.error(error);
      toast.error('Request Failure, Please Try Again Later', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const checkPlagRequestStatus = async (progId, taskId) => {
    try {
      const url = `${constants.base_url}/evalpro/get_plagiarism_task_status/${progId}`;
      const response = await httpService.post(url, { task_id: taskId });
      const taskStatus = response.data.data.status;

      if (taskStatus === 'FAILURE') {
        toast.error(response.data.data.result, { position: 'top-right', autoClose: 3000 });
      } else if (taskStatus === 'SUCCESS') {
        toast.success('Plagiarism Result Ready!', { position: 'top-right', autoClose: 3000 });
      } else {
        setTimeout(() => checkPlagRequestStatus(progId, taskId), 2000);
      }
    } catch (error) {
      log.error(error);
    }
  };

  // TA Buddy functions
  const getAiGradingRequestDetail = async () => {
    try {
      const response = await httpService.get(
        `${constants.base_url}/evalpro/rubric/fetch_ai_grading_request_detail/${programId}`
      );
      setAiGradingRequestDetail(response.data);
    } catch (error) {
      log.error('Failed to fetch AI grading request details:', error);
    }
  };

  const fetchAiGrades = async (taskId) => {
    try {
      const response = await httpService.get(
        `${constants.base_url}/evalpro/rubric/fetch_ai_grades/${programId}`,
        { params: { task_id: taskId } }
      );
      setAiGradingRequestDetail(response.data);
    } catch (error) {
      if (error.response?.status === 400) {
        displayGlobalMessage(error.response.data.detail || 'Task not found.', 'error');
        getAiGradingRequestDetail();
      } else if (error.response?.status === 202) {
        displayGlobalMessage(error.response.data.detail || 'Grading in Progress', 'info');
      } else {
        displayGlobalMessage('Failed to fetch AI grading status.', 'error');
      }
    }
  };

  const handleDiscardChanges = async (taskId) => {
    try {
      const response = await httpService.get(
        `${constants.base_url}/evalpro/rubric/delete_ai_grading_request_detail/${programId}`,
        { params: { task_id: taskId } }
      );
      setAiGradingRequestDetail(response.data);
    } catch (error) {
      log.error('Error in handleDiscardChanges:', error);
      displayGlobalMessage('Error while deleting', 'error');
    }
  };

  // Deposit Data function
  const handleExportSubmission = () => {
    if (submissionsSummary.rubric_grading_pending > submissionsSummary.rubric_graded) {
      displayGlobalMessage('Rubric Grading Not Done Yet', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('program_id', programId);
    formData.append('submissions', listOfAllSubmissions);

    const url = `${constants.base_url}/evalpro/rubric/exportData/${labID}`;

    httpService
      .post(url, formData)
      .then(() => {
        displayGlobalMessage('Data Exported Successfully', 'success');
        setDataExport(true);
      })
      .catch((error) => {
        if (error.response?.status === 500) {
          displayGlobalMessage('Data Already Exported', 'error');
        } else {
          displayGlobalMessage('Server error', 'error');
        }
      });
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    };
    return new Intl.DateTimeFormat('en-GB', options).format(date);
  };

  // Get submission status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      Submitted: { class: 'statusSuccess', label: 'Submitted' },
      'Compilation Error': { class: 'statusError', label: 'Compilation Error' },
      'Runtime Error': { class: 'statusWarning', label: 'Runtime Error' },
      'Time Limit Exceeded': { class: 'statusWarning', label: 'Time Limit Exceeded' },
      'Not Uploaded': { class: 'statusDefault', label: 'Not Uploaded' },
    };
    const statusInfo = statusMap[status] || { class: 'statusDefault', label: status || '-' };
    return <span className={`${styles.statusBadge} ${styles[statusInfo.class]}`}>{statusInfo.label}</span>;
  };

  // Get testcase result - display value directly like old code
  const getTestcaseResult = (result) => {
    if (result === undefined || result === null) {
      return '-';
    }
    // Return the value directly as the old code does
    return result;
  };

  // Toggle column visibility
  const toggleColumn = (column) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  // Toggle all columns
  const toggleAllColumns = (checked) => {
    setVisibleColumns((prev) => {
      const newState = {};
      Object.keys(prev).forEach((key) => {
        newState[key] = checked;
      });
      return newState;
    });
  };

  // Check if all columns are selected
  const allColumnsSelected = Object.values(visibleColumns).every((v) => v);

  // Clear filters
  const clearFilters = () => {
    setFilters({
      submissionStatus: '',
      evaluationStatus: '',
      rubricStatus: '',
      validity: '',
    });
  };

  // Check if any filter is active
  const hasActiveFilters = Object.values(filters).some((v) => v !== '');

  // Handle column sort click
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle sort order
      if (sortOrder === '') {
        setSortOrder('desc');
      } else if (sortOrder === 'desc') {
        setSortOrder('');
        setSortField('');
      } else {
        setSortOrder('');
        setSortField('');
      }
    } else {
      // New field - set ascending
      setSortField(field);
      setSortOrder('');
    }
  };

  // Get sort icon for column header
  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortOrder === 'desc' ? <FiChevronDown size={14} /> : <FiChevronUp size={14} />;
  };

  // Navigate to SubjectiveGrading page
  const handleFileClick = (submission) => {
    // Save submissions to localStorage before navigation (critical for grading page navigation)
    if (submissions && submissions.length > 0) {
      localStorage.setItem('submissionData', JSON.stringify(submissions));
    }

    navigate(`SubjectiveGrading/${submission.student || submission.user_id}`, {
      state: {
        program_id: programId,
        submission: submission,
        numOfRows: 100,
      },
    });
  };

  // Show testcase modal for practice/evaluate marks
  const handleMarksClick = (submission, type) => {
    setTestcaseModalData({
      submissionID: submission.id,
      testcaseType: type,
      studentName: submission.first_name || '',
      studentEmail: submission.email || '',
      obtainedMarks: type === 'visible_testcases' ? (submission.obtained_visible_marks ?? 0) : (submission.obtained_hidden_marks ?? 0),
      studentID: submission.student || submission.user_id,
    });
    setShowTestcaseModal(true);
  };

  // Show error modal for compilation/runtime errors
  const handleStatusClick = (submission, status) => {
    if (status === 'Compilation Error' || status === 'Runtime Error') {
      const errorData = {
        firstName: submission.first_name,
        email: submission.email,
        errorMessage: status === 'Compilation Error' ? submission.compiler_errors : submission.runtime_errors,
        errorType: status,
      };
      setErrorDetails(errorData);
      setShowErrorModal(true);
    }
  };

  // Column definitions for View dropdown
  const columnOptions = useMemo(() => [
    { key: 'rollNo', label: 'Roll No' },
    { key: 'email', label: 'Email' },
    { key: 'name', label: 'Name' },
    { key: 'uploadedOn', label: 'Uploaded On' },
    { key: 'evaluatedOn', label: 'Evaluated On' },
    { key: 'fileSubmitted', label: 'File Submitted' },
    { key: 'ta', label: 'TA' },
    { key: 'gradedBy', label: 'Graded By' },
    { key: 'gradedOn', label: 'Graded On' },
    { key: 'testcases', label: 'Testcases' },
    { key: 'practiceMarks', label: 'Total Practice Marks' },
    { key: 'evaluateMarks', label: 'Total Evaluate Marks' },
    { key: 'grade', label: 'Grade' },
    { key: 'rubricMarks', label: 'Rubrics Marks' },
    { key: 'manualMarks', label: 'Manual Marks' },
    { key: 'executionTime', label: 'Execution Time' },
    { key: 'indentation', label: 'Indentation Percentage' },
    { key: 'totalMarks', label: 'Total Marks' },
    { key: 'submissionStatus', label: 'Submission Status' },
  ], []);

  // Memoize summary data
  const memoizedSummary = useMemo(() => submissionsSummary, [
    submissionsSummary.total_students,
    submissionsSummary.total_submissions,
    submissionsSummary.evaluation_pending,
    submissionsSummary.evaluated,
    submissionsSummary.rubric_grading_pending,
    submissionsSummary.rubric_graded,
    submissionsSummary.invalid,
  ]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingWrapper}>
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb */}
      <BreadcrumbNav
        courseId={courseId}
        courseName={courseName}
        labID={labID}
        labTitle={labTitle}
        actType={actType}
        programId={programId}
        activityTitle={activityTitle}
      />

      {/* Statistics Cards */}
      <StatsRow submissionsSummary={memoizedSummary} />

      {/* Progress Status Row */}
      {(celeryTaskId || isInQueue) && (
        <div className={styles.progressRow}>
          {celeryTaskId ? (
            <div className={styles.progressContainer}>
              <Button variant="primary" disabled>
                Updating results... <Spinner as="span" animation="border" size="sm" />
              </Button>
              <ProgressBar
                animated
                striped
                now={progressPercentage}
                label={`${progressPercentage}%`}
                className={styles.progressBar}
              />
            </div>
          ) : (
            <span className={styles.statusWarningText}>Adding to Queue...</span>
          )}
        </div>
      )}

      {/* Search and Actions Bar */}
      <div className={styles.tableContainer}>
        <div className={styles.searchActionsBar}>
          <div className={styles.searchBar}>
            <FiSearch className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search by email, name, roll no..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <div className={styles.actionButtons}>
            {/* Run Dropdown */}
            <Dropdown>
              <Dropdown.Toggle variant="primary" className={styles.actionBtnWithArrow} disabled={isInQueue}>
                Run <FiChevronDown size={14} />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={(e) => {
                    if (testcases.visible?.length === 0) {
                      displayGlobalMessage('No visible testcases added', 'error');
                    } else {
                      executeSubmission(false, 'visible');
                    }
                  }}
                >
                  Visible Testcases
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={(e) => {
                    if (testcases.hidden?.length === 0) {
                      displayGlobalMessage('No hidden testcases added', 'error');
                    } else {
                      executeSubmission(false, 'hidden');
                    }
                  }}
                >
                  Hidden Testcases
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={(e) => {
                    if (testcases.visible?.length === 0 && testcases.hidden?.length === 0) {
                      displayGlobalMessage('No testcases added', 'error');
                    } else {
                      executeSubmission(false, 'all');
                    }
                  }}
                >
                  All Testcases
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            {/* Force Run Dropdown */}
            <Dropdown>
              <Dropdown.Toggle variant="primary" className={styles.actionBtnWithArrow} disabled={isInQueue}>
                Force Run <FiChevronDown size={14} />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={(e) => {
                    if (testcases.visible?.length === 0) {
                      displayGlobalMessage('No visible testcases added', 'error');
                    } else {
                      executeSubmission(true, 'visible');
                    }
                  }}
                >
                  Visible Testcases
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={(e) => {
                    if (testcases.hidden?.length === 0) {
                      displayGlobalMessage('No hidden testcases added', 'error');
                    } else {
                      executeSubmission(true, 'hidden');
                    }
                  }}
                >
                  Hidden Testcases
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={(e) => {
                    if (testcases.visible?.length === 0 && testcases.hidden?.length === 0) {
                      displayGlobalMessage('No testcases added', 'error');
                    } else {
                      executeSubmission(true, 'all');
                    }
                  }}
                >
                  All Testcases
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            {/* More Dropdown - Contains Download, Submit, Plagiarism, TA Allocation, TA Buddy, Deposit Data */}
            <Dropdown show={showMoreDropdown} onToggle={(isOpen) => setShowMoreDropdown(isOpen)}>
              <Dropdown.Toggle variant="primary" className={styles.actionBtnWithArrow}>
                More <FiChevronDown size={14} />
              </Dropdown.Toggle>
              <Dropdown.Menu className={styles.moreDropdownMenu}>
                {/* Download & Export Section */}
                <div className={styles.moreDropdownSection}>
                  <div className={styles.moreDropdownHeader}>Download & Export</div>
                  <DownloadSingleProgrammingActivityCSV
                    as="div"
                    program_id={programId}
                    activityTitle={activityTitle}
                    zipFormat={true}
                    lab_Id={labID}
                    lab_title={labTitle}
                    className={`${styles.moreDropdownItem} ${(!submissionsSummary.total_submissions || submissionsSummary.total_submissions === 0) ? styles.moreDropdownItemDisabled : ''}`}
                    disabled={!submissionsSummary.total_submissions || submissionsSummary.total_submissions === 0}
                  />
                  <DownloadSingleProgrammingActivityCSV
                    as="div"
                    program_id={programId}
                    activityTitle={activityTitle}
                    csvFormat={true}
                    lab_Id={labID}
                    lab_title={labTitle}
                    className={`${styles.moreDropdownItem} ${(!submissionsSummary.total_submissions || submissionsSummary.total_submissions === 0) ? styles.moreDropdownItemDisabled : ''}`}
                    disabled={!submissionsSummary.total_submissions || submissionsSummary.total_submissions === 0}
                  >
                    Download as CSV
                  </DownloadSingleProgrammingActivityCSV>
                  <div
                    className={`${styles.moreDropdownItem} ${dataExport ? styles.moreDropdownItemDisabled : ''}`}
                    onClick={() => !dataExport && handleExportSubmission()}
                  >
                    Deposit Data
                  </div>
                </div>

                {/* Submit Section */}
                <div className={styles.moreDropdownSection}>
                  <div className={styles.moreDropdownHeader}>Submit</div>
                  <div
                    className={`${styles.moreDropdownItem} ${(isInQueue || !accessControlVars.enable_submit_as_ta) ? styles.moreDropdownItemDisabled : ''}`}
                    onClick={() => {
                      if (!isInQueue && accessControlVars.enable_submit_as_ta) {
                        setShowPopupBulkSubmission(true);
                        setShowMoreDropdown(false);
                      }
                    }}
                  >
                    Bulk Submission
                  </div>
                </div>

                {/* Plagiarism Check Section */}
                <div className={styles.moreDropdownSection}>
                  <div className={styles.moreDropdownHeader}>Plagiarism Check</div>
                  <div
                    className={`${styles.moreDropdownItem} ${(isInQueue || !accessControlVars.enable_submit_as_ta) ? styles.moreDropdownItemDisabled : ''}`}
                    onClick={() => {
                      if (!isInQueue && accessControlVars.enable_submit_as_ta) {
                        setNewReqModal(true);
                        getPlagCoursesAndActivities();
                        setShowMoreDropdown(false);
                      }
                    }}
                  >
                    New Plagiarism Request
                  </div>
                  <div
                    className={`${styles.moreDropdownItem} ${(isInQueue || !accessControlVars.enable_submit_as_ta) ? styles.moreDropdownItemDisabled : ''}`}
                    onClick={() => {
                      if (!isInQueue && accessControlVars.enable_submit_as_ta) {
                        setShowRequestModal(true);
                        getPlagiarismRequests();
                        setShowMoreDropdown(false);
                      }
                    }}
                  >
                    Show Plagiarism Results
                  </div>
                </div>

                {/* TA Management Section */}
                <div className={styles.moreDropdownSection}>
                  <div className={styles.moreDropdownHeader}>TA Buddy</div>
                  <div
                    className={styles.moreDropdownItem}
                    onClick={() => {
                      setNewReqTABuddyModal(true);
                      setShowMoreDropdown(false);
                    }}
                  >
                    Create AI Grading Request
                  </div>
                  <div
                    className={styles.moreDropdownItem}
                    onClick={() => {
                      setShowRequestTABuddyModal(true);
                      getAiGradingRequestDetail();
                      setShowMoreDropdown(false);
                    }}
                  >
                    Show AI Grading Requests
                  </div>
                </div>
              </Dropdown.Menu>
            </Dropdown>

            {/* Filters Dropdown */}
            <Dropdown show={showFilterDropdown} onToggle={(isOpen) => setShowFilterDropdown(isOpen)}>
              <Dropdown.Toggle variant="primary" className={styles.filterButton}>
                <FiFilter size={14} />
                Filters
                {hasActiveFilters && <span className={styles.filterBadge}></span>}
                <FiChevronDown size={14} />
              </Dropdown.Toggle>
              <Dropdown.Menu className={styles.filterDropdownMenu}>
                <div className={styles.filterSection}>
                  <div className={styles.filterLabel}>Submission Status</div>
                  <select
                    value={filters.submissionStatus}
                    onChange={(e) => setFilters((prev) => ({ ...prev, submissionStatus: e.target.value }))}
                    className={styles.filterSelect}
                  >
                    <option value="">All</option>
                    <option value="submitted">Submitted</option>
                  </select>
                </div>
                <div className={styles.filterSection}>
                  <div className={styles.filterLabel}>Evaluation Status</div>
                  <select
                    value={filters.evaluationStatus}
                    onChange={(e) => setFilters((prev) => ({ ...prev, evaluationStatus: e.target.value }))}
                    className={styles.filterSelect}
                  >
                    <option value="">All</option>
                    <option value="pending">Pending</option>
                    <option value="evaluated">Evaluated</option>
                  </select>
                </div>
                <div className={styles.filterSection}>
                  <div className={styles.filterLabel}>Rubric Status</div>
                  <select
                    value={filters.rubricStatus}
                    onChange={(e) => setFilters((prev) => ({ ...prev, rubricStatus: e.target.value }))}
                    className={styles.filterSelect}
                  >
                    <option value="">All</option>
                    <option value="graded">Graded</option>
                    <option value="not_graded">Not Graded</option>
                  </select>
                </div>
                <div className={styles.filterSection}>
                  <div className={styles.filterLabel}>Validity</div>
                  <select
                    value={filters.validity}
                    onChange={(e) => setFilters((prev) => ({ ...prev, validity: e.target.value }))}
                    className={styles.filterSelect}
                  >
                    <option value="">All</option>
                    <option value="invalid">Invalid</option>
                  </select>
                </div>
                {hasActiveFilters && (
                  <div className={styles.filterActions}>
                    <button onClick={clearFilters} className={styles.clearFiltersBtn}>
                      Clear Filters
                    </button>
                  </div>
                )}
              </Dropdown.Menu>
            </Dropdown>

            {/* View Dropdown */}
            <Dropdown show={showViewDropdown} onToggle={(isOpen) => setShowViewDropdown(isOpen)}>
              <Dropdown.Toggle variant="primary" className={styles.viewButton}>
                View <FiChevronDown size={14} />
              </Dropdown.Toggle>
              <Dropdown.Menu className={styles.viewDropdownMenu}>
                <div className={styles.dropdownHeader}>TOGGLE COLUMNS</div>
                <Dropdown.Item as="div" className={styles.columnToggleItem} onClick={(e) => e.stopPropagation()}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={allColumnsSelected}
                      onChange={(e) => toggleAllColumns(e.target.checked)}
                    />
                    <span><strong>Select All</strong></span>
                  </label>
                </Dropdown.Item>
                <Dropdown.Divider />
                {columnOptions.map((col) => (
                  <Dropdown.Item key={col.key} as="div" className={styles.columnToggleItem} onClick={(e) => e.stopPropagation()}>
                    <label className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={visibleColumns[col.key]}
                        onChange={() => toggleColumn(col.key)}
                      />
                      <span>{col.label}</span>
                    </label>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {/* Submissions Table */}
        <div className={styles.tableWrapper} ref={tableWrapperRef} onScroll={handleScroll}>
          {tableLoading && (
            <div className={styles.tableLoadingOverlay}>
              <Spinner animation="border" size="sm" />
              <span>Loading...</span>
            </div>
          )}
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.checkboxCol}>
                  <input type="checkbox" checked={selectAll} onChange={(e) => handleSelectAll(e.target.checked)} />
                </th>
                <th className={styles.snoCol}>S.NO.</th>
                {visibleColumns.rollNo && (
                  <th className={`${styles.rollCol} ${styles.sortable}`} onClick={() => handleSort('roll_no')}>
                    ROLL NO {getSortIcon('roll_no')}
                  </th>
                )}
                {visibleColumns.email && (
                  <th className={`${styles.emailCol} ${styles.sortable}`} onClick={() => handleSort('email')}>
                    EMAIL {getSortIcon('email')}
                  </th>
                )}
                {visibleColumns.name && (
                  <th className={`${styles.nameCol} ${styles.sortable}`} onClick={() => handleSort('first_name')}>
                    NAME {getSortIcon('first_name')}
                  </th>
                )}
                {visibleColumns.uploadedOn && (
                  <th className={`${styles.dateCol} ${styles.sortable}`} onClick={() => handleSort('uploaded_on')}>
                    UPLOADED ON {getSortIcon('uploaded_on')}
                  </th>
                )}
                {visibleColumns.evaluatedOn && (
                  <th className={`${styles.dateCol} ${styles.sortable}`} onClick={() => handleSort('evaluated_on')}>
                    EVALUATED ON {getSortIcon('evaluated_on')}
                  </th>
                )}
                {visibleColumns.fileSubmitted && (
                  <th className={`${styles.fileCol} ${styles.sortable}`} onClick={() => handleSort('file_submitted')}>
                    FILE SUBMITTED {getSortIcon('file_submitted')}
                  </th>
                )}
                {visibleColumns.ta && (
                  <th className={`${styles.taCol} ${styles.sortable}`} onClick={() => handleSort('TA')}>
                    TA {getSortIcon('TA')}
                  </th>
                )}
                {visibleColumns.gradedBy && (
                  <th className={`${styles.taCol} ${styles.sortable}`} onClick={() => handleSort('graded_by')}>
                    GRADED BY {getSortIcon('graded_by')}
                  </th>
                )}
                {visibleColumns.gradedOn && (
                  <th className={`${styles.dateCol} ${styles.sortable}`} onClick={() => handleSort('graded_on')}>
                    GRADED ON {getSortIcon('graded_on')}
                  </th>
                )}
                {visibleColumns.testcases &&
                  testcases.visible?.map((tc) => (
                    <th key={`v-${tc.id}`} className={styles.testcaseCol}>V: {tc.name}</th>
                  ))}
                {visibleColumns.testcases &&
                  testcases.hidden?.map((tc) => (
                    <th key={`h-${tc.id}`} className={styles.testcaseCol}>H: {tc.name}</th>
                  ))}
                {visibleColumns.practiceMarks && (
                  <th className={`${styles.marksCol} ${styles.sortable}`} onClick={() => handleSort('obtained_visible_marks')}>
                    TOTAL PRACTICE MARKS {getSortIcon('obtained_visible_marks')}
                  </th>
                )}
                {visibleColumns.evaluateMarks && (
                  <th className={`${styles.marksCol} ${styles.sortable}`} onClick={() => handleSort('obtained_hidden_marks')}>
                    TOTAL EVALUATE MARKS {getSortIcon('obtained_hidden_marks')}
                  </th>
                )}
                {visibleColumns.grade && <th className={styles.gradeCol}>GRADE</th>}
                {visibleColumns.rubricMarks && <th className={styles.marksCol}>RUBRICS MARKS</th>}
                {visibleColumns.manualMarks && (
                  <th className={`${styles.marksCol} ${styles.sortable}`} onClick={() => handleSort('obtained_manual_marks')}>
                    MANUAL MARKS {getSortIcon('obtained_manual_marks')}
                  </th>
                )}
                {visibleColumns.executionTime && (
                  <th className={`${styles.timeCol} ${styles.sortable}`} onClick={() => handleSort('total_execution_time')}>
                    EXECUTION TIME {getSortIcon('total_execution_time')}
                  </th>
                )}
                {visibleColumns.indentation && (
                  <th className={`${styles.percentCol} ${styles.sortable}`} onClick={() => handleSort('indentation_percentage')}>
                    INDENTATION PERCENTAGE {getSortIcon('indentation_percentage')}
                  </th>
                )}
                {visibleColumns.totalMarks && (
                  <th className={`${styles.marksCol} ${styles.sortable}`} onClick={() => handleSort('total_marks')}>
                    TOTAL MARKS {getSortIcon('total_marks')}
                  </th>
                )}
                {visibleColumns.submissionStatus && (
                  <th className={`${styles.statusCol} ${styles.sortable}`} onClick={() => handleSort('submission_status')}>
                    SUBMISSION STATUS {getSortIcon('submission_status')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {submissions.length === 0 ? (
                <tr>
                  <td colSpan={30} className={styles.emptyState}>No submissions found</td>
                </tr>
              ) : (
                submissions.map((submission, index) => {
                  const isSelected = selectedSubmissions.some((s) => s.id === submission.id);
                  const status = submission.submission_status;
                  const isClickableStatus = status === 'Compilation Error' || status === 'Runtime Error';
                  return (
                    <tr key={submission.id || index} className={styles.tableRow}>
                      <td className={styles.checkboxCell}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleRowSelect(submission, e.target.checked)}
                        />
                      </td>
                      <td className={styles.snoCell}>{index + 1}</td>
                      {visibleColumns.rollNo && <td className={styles.rollCell}>{submission.roll_no || '-'}</td>}
                      {visibleColumns.email && <td className={styles.emailCell}>{submission.email || '-'}</td>}
                      {visibleColumns.name && <td className={styles.nameCell}>{submission.first_name || '-'}</td>}
                      {visibleColumns.uploadedOn && (
                        <td className={styles.dateCell}>{formatTimestamp(submission.uploaded_on)}</td>
                      )}
                      {visibleColumns.evaluatedOn && (
                        <td className={styles.dateCell}>{formatTimestamp(submission.evaluated_on)}</td>
                      )}
                      {visibleColumns.fileSubmitted && (
                        <td className={styles.fileCell}>
                          {submission.names_of_files_submitted?.length > 0 ? (
                            <span className={styles.fileLink} onClick={() => handleFileClick(submission)}>
                              {submission.names_of_files_submitted.join(', ')}
                            </span>
                          ) : '-'}
                        </td>
                      )}
                      {visibleColumns.ta && (
                        <td className={styles.taCell}>
                          {submission['batch-ta-data']?.name || 'Not Alloted, edit lab to allocate'}
                        </td>
                      )}
                      {visibleColumns.gradedBy && (
                        <td className={styles.taCell}>
                          {submission.graded_by === undefined || submission.graded_by === 'Not Graded'
                            ? 'Not Graded'
                            : (submission.graded_by_data?.name || submission.graded_by || 'Not Graded')}
                        </td>
                      )}
                      {visibleColumns.gradedOn && (
                        <td className={styles.dateCell}>
                          {submission.graded_on ? formatTimestamp(submission.graded_on) : 'Not Graded'}
                        </td>
                      )}
                      {visibleColumns.testcases &&
                        testcases.visible?.map((tc) => (
                          <td key={`v-${tc.id}`} className={styles.testcaseCell}>
                            {getTestcaseResult(submission.testcases?.[tc.id])}
                          </td>
                        ))}
                      {visibleColumns.testcases &&
                        testcases.hidden?.map((tc) => (
                          <td key={`h-${tc.id}`} className={styles.testcaseCell}>
                            {getTestcaseResult(submission.testcases?.[tc.id])}
                          </td>
                        ))}
                      {visibleColumns.practiceMarks && (
                        <td className={styles.marksCell}>
                          <span className={styles.marksLink} onClick={() => handleMarksClick(submission, 'visible_testcases')}>{submission.obtained_visible_marks ?? 0}</span>
                        </td>
                      )}
                      {visibleColumns.evaluateMarks && (
                        <td className={styles.marksCell}>
                          <span className={styles.marksLink} onClick={() => handleMarksClick(submission, 'hidden_testcases')}>{submission.obtained_hidden_marks ?? 0}</span>
                        </td>
                      )}
                      {visibleColumns.grade && (
                        <td className={styles.gradeCell}>
                          <button className={styles.gradeButton} onClick={() => handleFileClick(submission)}>
                            Grade
                          </button>
                        </td>
                      )}
                      {visibleColumns.rubricMarks && (
                        <td className={styles.marksCell}>{submission.obtained_rubric_marks ?? 0}</td>
                      )}
                      {visibleColumns.manualMarks && (
                        <td className={styles.marksCell}>
                          <input
                            type="number"
                            className={styles.manualMarksInput}
                            defaultValue={submission.obtained_manual_marks ?? 0}
                          />
                        </td>
                      )}
                      {visibleColumns.executionTime && (
                        <td className={styles.timeCell}>{submission.total_execution_time ?? 0}</td>
                      )}
                      {visibleColumns.indentation && (
                        <td className={styles.percentCell}>{submission.indentation_percentage ?? -1}</td>
                      )}
                      {visibleColumns.totalMarks && (
                        <td className={styles.marksCell}>{submission.total_marks ?? 0}</td>
                      )}
                      {visibleColumns.submissionStatus && (
                        <td className={styles.statusCell}>
                          {isClickableStatus ? (
                            <span
                              className={`${styles.statusLink} ${styles.statusError}`}
                              onClick={() => handleStatusClick(submission, status)}
                            >
                              {status}
                            </span>
                          ) : (
                            getStatusBadge(status)
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          {isLoadingMore && (
            <div className={styles.loadingMoreRow}>
              <Spinner animation="border" size="sm" />
            </div>
          )}
        </div>
      </div>

      {/* Error Modal */}
      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{errorDetails.errorType}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={styles.errorModalInfo}>
            <strong>Name:</strong> {errorDetails.firstName} <br />
            <strong>Email:</strong> {errorDetails.email}
          </div>
          <hr />
          <div className={styles.errorModalContent}>
            <textarea
              className={styles.errorTextarea}
              value={errorDetails.errorMessage || 'No error details available'}
              readOnly
            />
          </div>
        </Modal.Body>
      </Modal>

      {/* Testcase Modal for Practice/Evaluate Marks */}
      <TestCaseModal
        showModal={showTestcaseModal}
        setShowModal={setShowTestcaseModal}
        submissionID={testcaseModalData.submissionID}
        testcaseType={testcaseModalData.testcaseType}
        studentName={testcaseModalData.studentName}
        studentEmail={testcaseModalData.studentEmail}
        obtainedMarks={testcaseModalData.obtainedMarks}
        studentID={testcaseModalData.studentID}
      />

      {/* Bulk Submission Modal */}
      <BulkSubmissionModal
        showPopupBulkSubmission={showPopupBulkSubmission}
        setShowPopupBulkSubmission={setShowPopupBulkSubmission}
        setBulkSubmissionStatus={setBulkSubmissionStatus}
        program_id={programId}
      />

      {/* TA Buddy Component - has its own modal */}
      <TaBuddy
        criteriaState={criteriaState}
        setCriteriaState={setCriteriaState}
        criteriasWithAiGrading={criteriasWithAiGrading}
        isSelectAllChecked={isSelectAllChecked}
        selectedSubmissions={selectedSubmissions}
        newReqModal={newReqTABuddyModal}
        handleModalClose={handleTABuddyModalClose}
        listOfAllSubmissions={listOfAllSubmissions}
        program_id={programId}
        aiGradingList={aiGradingList}
        setAiGradingList={setAiGradingList}
      />

      {/* TA Buddy Show Requests Modal */}
      <Modal show={showRequestTABuddyModal} onHide={() => setShowRequestTABuddyModal(false)} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>AI Grading Requests</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: '500px' }}>
          <table className="table">
            <thead>
              <tr>
                <th style={{ maxWidth: '120px' }}>Criteria</th>
                <th style={{ whiteSpace: 'normal', maxWidth: '150px' }}>Created at</th>
                <th style={{ maxWidth: '100px' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {aiGradingRequestDetail.length === 0 ? (
                <tr>
                  <td colSpan={3}>No grading requests found.</td>
                </tr>
              ) : (
                aiGradingRequestDetail.map((req, idx) => (
                  <tr key={idx}>
                    <td style={{ maxWidth: '120px' }}>{req.criteria}</td>
                    <td style={{ whiteSpace: 'normal', maxWidth: '150px', wordWrap: 'break-word' }}>
                      {req.created_at}
                    </td>
                    <td style={{ maxWidth: '100px' }}>
                      {req.status === 'pending' ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                          <span className="px-2 py-1 rounded-full text-xs font-semibold capitalize bg-yellow-100 text-yellow-800">
                            {req.status}
                          </span>
                          <span
                            style={{ cursor: 'pointer', color: '#007bff' }}
                            onClick={() => fetchAiGrades(req.task_id)}
                            title="Get Results"
                          >
                            ⬇
                          </span>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${req.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {req.status}
                          </span>
                          <span
                            style={{ cursor: 'pointer', color: '#dc3545' }}
                            onClick={() => handleDiscardChanges(req.task_id)}
                            title="Discard"
                          >
                            <GiCancel />
                          </span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Modal.Body>
      </Modal>

      {/* Plagiarism New Request Modal */}
      <Modal show={newReqModal} onHide={handlePlagModalClose} size="lg">
        <Modal.Header
          style={{
            backgroundColor: '#FFFFFF',
            paddingLeft: '30px',
            paddingRight: '30px',
          }}
          closeButton
        >
          <Modal.Title style={{ fontWeight: '500', fontSize: '24px' }}>New Plagiarism Request</Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            backgroundColor: '#FFFFFF',
            paddingLeft: '30px',
            paddingRight: '30px',
            paddingTop: '10px',
          }}
        >
          <Form>
            <Form.Group controlId="formSubmissionsAdded" style={{ marginBottom: '10px' }}>
              <Form.Label style={{ fontWeight: '500', fontSize: '16px', marginBottom: '4px' }}>
                Submissions Added<br />(Course Name - Year - Lab Name - Activity Name)
              </Form.Label>
              <div
                style={{
                  backgroundColor: '#f9fafb',
                  height: '100px',
                  overflow: 'auto',
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  paddingTop: '6px',
                  borderRadius: '6px',
                }}
              >
                {selectedSubmissionsForPlag.map((item) => (
                  <div
                    key={item.program_id}
                    style={{
                      backgroundColor: '#ffffff',
                      marginBottom: '6px',
                      paddingLeft: '6px',
                      paddingRight: '6px',
                      borderRadius: '3px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span>{item.course_title} - {item.course_year} - {item.lab_title} - {item.program_title}</span>
                    <span
                      style={{ fontWeight: '600', cursor: 'pointer', lineHeight: '20px' }}
                      onClick={() => handleRemoveSubmissionPlag(item.program_id)}
                    >
                      x
                    </span>
                  </div>
                ))}
              </div>
            </Form.Group>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <Form.Group controlId="formCourse" style={{ width: '40%' }}>
                <Form.Label style={{ fontWeight: '500', fontSize: '16px', marginBottom: '4px' }}>Course</Form.Label>
                <Form.Control
                  as="select"
                  value={courseSelectedForPlag}
                  onChange={(e) => {
                    setCourseSelectedForPlag(e.target.value);
                    setActivitySelectedForPlag(0);
                  }}
                  style={{ fontWeight: '400', color: '#666666', fontSize: '16px' }}
                >
                  <option value="">Select Course</option>
                  {coursesForPlag.map((item) => (
                    <option value={item} key={item}>{item}</option>
                  ))}
                </Form.Control>
              </Form.Group>

              <Form.Group controlId="formLabActivity" style={{ marginLeft: '4%', width: '40%' }}>
                <Form.Label style={{ fontWeight: '500', fontSize: '16px', marginBottom: '4px' }}>Select Lab Activity</Form.Label>
                <Form.Control
                  as="select"
                  value={activitySelectedForPlag}
                  onChange={(e) => setActivitySelectedForPlag(e.target.value)}
                  style={{ fontWeight: '400', color: '#666666', fontSize: '16px' }}
                >
                  <option value={0}>Select Lab</option>
                  {courseMap &&
                    courseMap[courseSelectedForPlag] &&
                    courseMap[courseSelectedForPlag].map((item) => (
                      <option value={item.program_id} key={item.program_id}>
                        {item.lab_title} - {item.program_title}
                      </option>
                    ))}
                </Form.Control>
              </Form.Group>

              <Button
                variant="primary"
                onClick={handleAddSubmissionPlag}
                style={{
                  marginLeft: '4%',
                  width: '12%',
                  fontWeight: '400',
                  fontSize: '16px',
                  marginTop: '28px',
                  height: '38px',
                }}
              >
                Add
              </Button>
            </div>

            <Form.Group controlId="formPlagChecker">
              <Form.Label style={{ fontWeight: '500', fontSize: '16px', marginBottom: '4px' }}>Plag Checker</Form.Label>
              <Form.Control
                as="select"
                value={plagiarismType}
                onChange={(e) => {
                  setPlagiarismType(e.target.value);
                  if (e.target.value === 'fett' || e.target.value === 'combined') {
                    setFettState(true);
                  } else {
                    setFettState(false);
                  }
                }}
                style={{ fontWeight: '400', color: '#666666', fontSize: '16px' }}
              >
                <option value="">Select Program</option>
                <option value="moss">MOSS</option>
                <option value="fett">FETT</option>
                <option value="combined">Combined (MOSS + FETT)</option>
              </Form.Control>
            </Form.Group>
          </Form>

          {fettState && (
            <div style={{ color: 'red', marginTop: '10px' }}>
              Warning:<br />
              FETT only works for Successfully Compiled CPP Submissions
            </div>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{
            backgroundColor: '#FFFFFF',
            paddingLeft: '30px',
            paddingRight: '30px',
          }}
        >
          <Button variant="secondary" onClick={handlePlagModalClose} style={{ fontWeight: '400', fontSize: '16px' }}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSubmitPlagRequest} style={{ fontWeight: '400', fontSize: '16px' }}>
            Get Plagiarism Result
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Plagiarism Show Requests Modal */}
      <Modal show={showRequestModal} onHide={() => setShowRequestModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Plagiarism Results</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: '500px' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Activities</th>
                <th style={{ whiteSpace: 'normal', maxWidth: '150px' }}>Created at</th>
                <th style={{ maxWidth: '120px' }}>Plag Type</th>
                <th style={{ maxWidth: '100px' }}>Result</th>
              </tr>
            </thead>
            <tbody>
              {plagiarismReqData.length === 0 ? (
                <tr>
                  <td colSpan={4}>No available options</td>
                </tr>
              ) : (
                plagiarismReqData.map((req, idx) => (
                  <tr key={idx}>
                    <td style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>{req.files}</td>
                    <td style={{ whiteSpace: 'normal', maxWidth: '150px', wordWrap: 'break-word' }}>{req.created_at}</td>
                    <td style={{ maxWidth: '120px' }}>{req.plag_checker}</td>
                    <td style={{ maxWidth: '100px' }}>
                      {req.status === 'Pending' ? (
                        <span>Pending</span>
                      ) : (
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            const url = `/courseware/course/${courseId}/content/labs/${labID}/programming/${programId}/ViewPlagiarismResult/${req.plag_checker}?ids=${req.ids.join(',')}`;
                            window.open(url, '_blank');
                          }}
                        >
                          View
                        </a>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ActivitySubmissionsV2;
